<script setup lang="ts">
import { ref, computed, nextTick, onMounted, toRefs } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

import DKRichTextHtml from './DKRichTextHtml.vue';
import DKRichTextJson from './DKRichTextJson.vue';

type RichTextJson = { json: any };

const props = withDefaults(
  defineProps<{
    text: string | RichTextJson;
    isAnimated?: boolean;
    imageStack?: string;
  }>(),
  {
    isAnimated: false,
    imageStack: undefined,
  }
);

const { text, isAnimated } = toRefs(props);

const isVisible = ref(false);
const richTextRef = ref<HTMLDivElement>();

const isHTML = computed<boolean>(
  () => typeof text.value === 'string' && /<\/?[a-z][\s\S]*>/i.test(text.value)
);

const isJSON = computed<boolean>(
  () => typeof text.value === 'object' && text.value.json
);

const htmlText = computed<string | undefined>(() =>
  isHTML.value ? (text.value as string) : undefined
);

const json = computed<RichTextJson | undefined>(() =>
  isJSON.value ? (text.value as RichTextJson) : undefined
);

const visibleCondition = computed<boolean>(
  () => isVisible.value || !isAnimated.value
);

const intersect = (): void => {
  useIntersectionObserver(
    richTextRef.value,
    ([{ isIntersecting }]) => {
      if (isIntersecting) {
        isVisible.value = isIntersecting;
      }
    },
    { threshold: 0.25 }
  );
};

onMounted(() => {
  if (isAnimated.value) nextTick(intersect);
});
</script>

<template>
  <div
    ref="richTextRef"
    :class="['dk-rich-text', { 'is-visible': visibleCondition }]"
  >
    <p v-if="!isJSON && !isHTML">
      {{ text }}
    </p>
    <DKRichTextHtml v-else-if="isHTML && htmlText" :html="htmlText" />
    <DKRichTextJson
      v-else-if="isJSON && json && visibleCondition"
      :json="json.json"
      :image-stack="imageStack"
    />
  </div>
</template>

<style lang="postcss">
.dk-rich-text {
  @apply delay-100;
  @apply transition duration-500;
  @apply p-small;

  &:not(.is-visible) {
    @apply opacity-0;
    @apply translate-y-6;
  }

  & div {
    & :where(h2, h3, h4, h5, h6) {
      @apply py-4;
    }

    &
      :where(
        p:not(:last-child),
        a.dk-link,
        ul,
        ol,
        img,
        hr,
        table,
        blockquote,
        .video,
        video:not(.video__player)
      ) {
      @apply mb-6;
    }

    & h2 {
      @apply text-s-h5 leading-s-h5;
      @apply font-familySemibold;

      @apply lg:heading-5;
    }

    & h3 {
      @apply text-s-h6 leading-s-h6;
      @apply font-familySemibold;

      @apply heading-6;
    }

    & h4 {
      @apply text-s-h7 leading-s-h7;
      @apply font-familySemibold;

      @apply heading-7;
    }

    & h5 {
      @apply p-large;
      @apply font-familySemibold;
    }

    & h6 {
      @apply p-medium;
      @apply font-familySemibold;
    }

    & a {
      &.dk-link {
        @apply inline-block;
      }

      &:not(.dk-link) {
        @apply text-blue-dark underline;

        &:hover {
          @apply text-red-dk;
        }
      }
    }

    & p {
      @apply hyphenate;
      @apply p-medium;
      word-break: auto-phrase;

      & :is(strong, b) {
        @apply font-familySemibold;
      }
    }

    & :is(ul, ol) {
      @apply list-disc;
      @apply list-outside;
      @apply pl-4;

      & li {
        @apply marker:text-red-dark;

        & p {
          @apply py-2;
          @apply mb-0;
        }
      }

      & :is(ul, ol) {
        @apply pl-5;
        @apply mb-0;
      }
    }

    & :is(img, video, .video) {
      @apply w-full;
      @apply aspect-video;
    }

    & blockquote {
      @apply px-6 py-4;
      @apply border-l-4 border-grey-cool-10;
    }

    & table {
      @apply w-full;
      @apply block;
      @apply overflow-x-auto;
      @apply whitespace-nowrap;

      @apply lg:whitespace-normal;
      @apply lg:table;

      & p {
        @apply mb-0;
        @apply break-normal;
      }

      & :is(th, td) {
        @apply py-2 pr-4;
        @apply p-extra-small;
        @apply border-b border-grey-cool-10;
        @apply lg:p-small;
      }

      & tbody td[rowspan]:hover {
        @apply bg-grey-cool-5;
      }

      & tbody td[rowspan]:hover ~ td:not([rowspan]) {
        @apply bg-[transparent];
      }

      & tbody tr:hover td:not([rowspan]) {
        @apply bg-grey-cool-5;
      }

      & :is(tr > th:last-of-type, tr > td:last-of-type) {
        @apply pr-0;
      }

      & tr:first-of-type > td {
        @apply border-t border-grey-cool-10;
      }

      & thead tr > td {
        @apply p-extra-small;
        @apply font-familySemibold;
        @apply border-t-0;

        @apply lg:p-small-bold;
        @apply lg:font-familySemibold;
      }
    }
  }
}
</style>
